/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback } from "react"

import eq from "lodash-es/eq"

import { reduxBatch } from "@/3514/utils"

import type {
    TCoachingChatSeedMutationContext as TSeedAnswerContext,
    TCoachingChatSeedMutationContext as TSeedAnswerMutationContext,
    TCoachingChatSeedMutationError as TSeedAnswerMutationError,
    TCoachingChatSeedMutationOnErrorFunction as TSeedAnswerMutationOnErrorFunction,
    TCoachingChatSeedMutationOnMutateFunction as TSeedAnswerMutationOnMutateFunction,
    TCoachingChatSeedMutationOnSuccessFunction as TSeedAnswerMutationOnSuccessFunction,
    TCoachingChatSeedMutationParams as TSeedAnswerMutationParams,
    TCoachingChatSeedMutationResponse as TSeedAnswerResponse
} from "../api"
import {
    useCoachingChatConversationDispatcher as useConversationDispatcher,
    useCoachingChatConversationStatusSelector as useConversationStatusSelector,
    useCoachingChatFormDispatcher as useFormDispatcher
} from "../store"
import { ECoachingChatConversationStatus as EConversationStatus } from "../types"
import { coachingChatUiUtils as uiUtils } from "../utils"

const {
    formExtractors: { getFormErrorFromMutationError }
} = uiUtils

type TFormMutationOptions = {
    onMutate: TSeedAnswerMutationOnMutateFunction
    onSuccess: TSeedAnswerMutationOnSuccessFunction
    onError: TSeedAnswerMutationOnErrorFunction
}

function useFormMutationOptions(entityId: number): ReturnType<(entityId: number) => TFormMutationOptions> {
    const formDispatcher: ReturnType<typeof useFormDispatcher> = useFormDispatcher(entityId)

    const conversationDispatcher: ReturnType<typeof useConversationDispatcher> = useConversationDispatcher(entityId)

    const conversationStatus: EConversationStatus = useConversationStatusSelector(entityId)

    const isConversationAvailable: boolean = !eq(conversationStatus, EConversationStatus.NotAvailable)

    const onMutate: TSeedAnswerMutationOnMutateFunction = useCallback(
        async ({ question_uuid, answer }: TSeedAnswerMutationParams): Promise<TSeedAnswerMutationContext> => {
            formDispatcher.populateAnswer({ id: question_uuid, answer })

            return { questionUuid: question_uuid, answer }
        },
        []
    )

    const onSuccess: TSeedAnswerMutationOnSuccessFunction = useCallback(
        async (
            { data }: TSeedAnswerResponse,
            _vars: TSeedAnswerMutationParams,
            _context: TSeedAnswerContext
        ): Promise<void> =>
            reduxBatch((): void => {
                if (isConversationAvailable) {
                    formDispatcher.clearError()

                    if (eq(data.all_answered, true)) {
                        conversationDispatcher.changeStatus(EConversationStatus.ReadyToStart)
                    } else {
                        conversationDispatcher.changeStatus(EConversationStatus.Locked)
                    }
                }
            }),
        [isConversationAvailable]
    )

    const onError: TSeedAnswerMutationOnErrorFunction = useCallback(
        async (
            error: TSeedAnswerMutationError,
            _vars: TSeedAnswerMutationParams,
            { questionUuid }: TSeedAnswerMutationContext
        ): Promise<void> =>
            reduxBatch((): void => {
                formDispatcher.populateAnswer({ id: questionUuid, answer: String("") })
                formDispatcher.triggerError(getFormErrorFromMutationError(error))
            }),
        []
    )

    return { onMutate, onSuccess, onError }
}

export { useFormMutationOptions as useCoachingChatFormMutationOptions }
export type { TFormMutationOptions as TCoachingChatFormMutationOptions }
